import { TRANSITION_DURATION } from '../constants'
import { paperStyles, contentStyles } from './modal.styles'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SxProps, Theme, useTheme } from '@mui/material/styles'
import { DialogProps } from '@mui/material/Dialog'
import useWindowSize from '../hooks/useWindowSize'
import useResize from '../hooks/useResize'
import DraggableBase from 'react-draggable'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'

const getMaxWidth = (maxWidth: Props['maxWidth'], theme: Theme) => {
  if (maxWidth === false) return 'none'
  if (typeof maxWidth === 'number') return maxWidth
  return theme.breakpoints.values[maxWidth || 'lg']
}

type Props = {
  open: boolean
  keepMounted?: boolean
  hidden?: boolean
  fullscreen?: boolean
  framedFullscreen?: boolean
  disableOpenAnimation?: boolean
  draggable?: boolean
  resizable?: boolean
  position: { x: number; y: number }
  size?: { width: number; height: number }
  onPositionChange?: (position: { x: number; y: number }) => void
  onSizeChange?: (size: { width: number; height: number }) => void
  zIndex?: number
  maxWidth?: DialogProps['maxWidth'] | number | false
  onFocus?: () => void
  onEntered?: () => void
  color?: string
  sx?: SxProps<Theme>
  children: React.ReactNode
}

const MIN_WIDTH = 480
const MIN_HEIGHT = 320

export default function ModalWindow({
  open,
  keepMounted,
  hidden,
  fullscreen,
  framedFullscreen,
  disableOpenAnimation,
  draggable = true,
  resizable = false,
  position,
  size,
  onPositionChange,
  onSizeChange,
  zIndex,
  maxWidth,
  onFocus,
  onEntered,
  color,
  sx,
  children,
}: Props) {
  const nodeRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const theme = useTheme()
  const { width: viewportWidth, height: viewportHeight } = useWindowSize()
  const [isDragging, setIsDragging] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [shouldDisplay, setShouldDisplay] = useState(!hidden)
  const prevFullscreenRef = useRef(fullscreen)

  useEffect(() => {
    if (!hidden) {
      setShouldDisplay(true)
    } else {
      const timer = setTimeout(() => setShouldDisplay(false), TRANSITION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [hidden])

  // Disable drag during fullscreen transitions so the animation plays uninterrupted
  useEffect(() => {
    if (prevFullscreenRef.current !== fullscreen) {
      prevFullscreenRef.current = fullscreen
      if (!disableOpenAnimation) {
        setIsTransitioning(true)
        const timer = setTimeout(() => setIsTransitioning(false), TRANSITION_DURATION)
        return () => clearTimeout(timer)
      }
    }
  }, [fullscreen, disableOpenAnimation])

  const isHidden = Boolean(hidden)
  const transitionIn = open && !isHidden

  const resolvedMaxWidth = getMaxWidth(maxWidth, theme)
  const defaultSize = useMemo(() => {
    const fallbackWidth =
      typeof resolvedMaxWidth === 'number'
        ? resolvedMaxWidth
        : viewportWidth
          ? Math.min(theme.breakpoints.values.lg, viewportWidth - 32)
          : theme.breakpoints.values.lg
    return {
      width: Math.max(Math.min(fallbackWidth, Math.max(viewportWidth - 32, MIN_WIDTH)), MIN_WIDTH),
      height: Math.max(Math.min(Math.round(viewportHeight * 0.6), Math.max(viewportHeight - 32, MIN_HEIGHT)), MIN_HEIGHT),
    }
  }, [resolvedMaxWidth, viewportHeight, viewportWidth, theme.breakpoints.values.lg])

  const resolvedSize = size ?? defaultSize

  const { isResizing, resizeCursor, resizeHandlers } = useResize({
    size: resolvedSize,
    position,
    minSize: { width: MIN_WIDTH, height: MIN_HEIGHT },
    elementRef: contentRef,
    onSizeChange,
    onPositionChange,
    enabled: resizable && !fullscreen,
  })

  const controlledPosition = fullscreen
    ? { x: framedFullscreen ? 16 : 0, y: framedFullscreen ? 16 : 0 }
    : position
  const width = fullscreen ? (framedFullscreen ? Math.max(viewportWidth - 32, 0) : viewportWidth) : resolvedSize.width
  const height = fullscreen ? (framedFullscreen ? Math.max(viewportHeight - 32, 0) : viewportHeight) : resolvedSize.height

  const content = (
    <Box ref={contentRef} sx={{ ...contentStyles(color, fullscreen && !framedFullscreen ? 'none' : 'solid'), ...sx }}>
      {children}
    </Box>
  )

  useEffect(() => {
    if (!open || isHidden || !onEntered) return
    const timeout = disableOpenAnimation ? 0 : TRANSITION_DURATION
    const timer = window.setTimeout(() => onEntered(), timeout)
    return () => window.clearTimeout(timer)
  }, [open, isHidden, onEntered, disableOpenAnimation])

  if (!open && !keepMounted) return null

  return (
    <DraggableBase
      nodeRef={nodeRef}
      cancel=".terminal-content, .MuiInputBase-input, pre, .xterm, .MuiButtonBase-root"
      disabled={!draggable || fullscreen || isResizing || isTransitioning}
      position={controlledPosition}
      onStart={e => {
        e.stopPropagation()
        setIsDragging(true)
      }}
      onStop={(_, data) => {
        setIsDragging(false)
        onPositionChange?.({ x: data.x, y: data.y })
      }}
    >
      <Box
        ref={nodeRef}
        {...resizeHandlers}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width,
          height,
          zIndex,
          display: shouldDisplay ? 'block' : 'none',
          pointerEvents: transitionIn ? 'auto' : 'none',
          cursor: resizeCursor ?? (isDragging ? 'grabbing' : draggable && !fullscreen ? 'grab' : 'default'),
          opacity: transitionIn ? 1 : 0,
          transition: disableOpenAnimation || isDragging || isResizing
            ? 'none'
            : theme.transitions.create(['width', 'height', 'opacity', 'transform'], {
                duration: TRANSITION_DURATION,
                easing: theme.transitions.easing.easeInOut,
              }),
        }}
      >
        <Paper
          elevation={0}
          onClick={e => e.stopPropagation()}
          onPointerDown={onFocus}
          sx={{
            width: '100%',
            height: '100%',
            overflow: 'visible',
            pointerEvents: transitionIn ? 'auto' : 'none',
            ...(fullscreen && !framedFullscreen ? { cursor: 'default', borderRadius: 0 } : paperStyles),
          }}
        >
          {content}
        </Paper>
      </Box>
    </DraggableBase>
  )
}
